import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { StatusSolicitacao } from '../../../core/constants/status-solicitacao';
import { DetalhesResponse } from '../../../core/models/resumo';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';
import { formatarImpostoComAliquota, formatarMoeda } from '../../../shared/utils/formatadores';
import { ExtratoDesligamentoTokenResponse } from '../../../core/models/extrato-desligamento';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { base64ParaPdf } from '../../../shared/utils/pdf';

/** Texto exibido para cada código de statusSolicitacao da API; códigos fora da lista aparecem como vieram. */
const TEXTO_STATUS: Record<string, string> = {
  EM_PREENCHIMENTO: 'Em preenchimento',
  AGUARDANDO_ESCOLHA_INSTITUTO: 'Aguardando escolha do Instituto',
  AGUARDANDO_DADOS_CADASTRAIS: 'Aguardando dados cadastrais',
  AGUARDANDO_TIPO_IR: 'Aguardando modalidade de IR',
  AGUARDANDO_ENVIO_DOCUMENTOS: 'Aguardando envio de documentos',
  AGUARDANDO_VALIDACAO_DOCUMENTOS: 'Aguardando validação de documentos',
  AGUARDANDO_REVISAO: 'Aguardando revisão',
  AGUARDANDO_ASSINATURA_TERMO: 'Aguardando assinatura do termo',
  FINALIZADO: 'Concluído',
  CANCELADO: 'Cancelado',
};

@Component({
  imports: [PdfDocument],
  selector: 'app-detalhes-instituto',
  styleUrl: './detalhes-instituto.scss',
  templateUrl: './detalhes-instituto.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DetalhesInstituto implements OnInit {
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly detalhes = signal<DetalhesResponse | null>(null);
  readonly openModal = signal(false);
  readonly openDocModal = signal(false);
  

  /** Valores de statusSolicitacao que mudam o card de status exibido. */
  protected readonly statusDocumentosEmAnalise = 'Documentos em análise';
  protected readonly statusDocumentacaoNaoValidada = 'Documentação não validada';

  /** Extrato de desligamento (URL da API), usado só no download. */
  readonly pdfSrc = signal<string | undefined>(undefined);
  /** Termo assinado (blob gerado do base64 de getDetalhes), exibido no visualizador. */
  readonly pdfSrcTermo = signal<string | undefined>(undefined);

  /** Solicitação finalizada: a escolha de Instituto Online não pode mais ser cancelada. */
  readonly cancelamentoBloqueado = computed(
    () => this.detalhes()?.statusSolicitacao === StatusSolicitacao.FINALIZADO,
  );

  /** Termo ainda não assinado: o aviso aparece em amarelo, sem o ícone de check. */
  readonly termoPendente = computed(() => this.detalhes()?.termoAssinado === false);

  /**
   * Texto ao lado do ícone de check no visualizador. Com o termo ainda não assinado (termoAssinado
   * false), avisa que ele está sendo gerado; assinado, mostra a data (vazio se a API não a informa).
   */
  readonly textoVisualizado = computed(() => {
    const detalhes = this.detalhes();
    if (!detalhes) return '';
    if (this.termoPendente()) return 'Seu termo assinado está sendo gerado. Por favor, volte em alguns minutos para acessá-lo.';

    const dataAssinatura = detalhes.dataAssinaturaTermo;
    return dataAssinatura
      ? `Assinado com sucesso em ${formatDate(dataAssinatura, "dd/MM/yyyy 'às' HH:mm", 'en-US')}`
      : '';
  });

  /** Valores já formatados para exibição; nulo enquanto a API não responde. */
  protected readonly exibicao = computed(() => {
    const detalhes = this.detalhes();
    if (!detalhes) return null;

    return {
      protocolo: detalhes.protocolo,
      status: TEXTO_STATUS[detalhes.statusSolicitacao] ?? detalhes.statusSolicitacao,
      opcao: detalhes.tipoInstitutoOnlineEscolhido,
      primeiraParcela: detalhes.primeiraParcela ? formatDate(detalhes.primeiraParcela, 'dd/MM/yyyy', 'en-US') : '-',
      numeroParcelas: detalhes.numeroParcelas,
      valorPrimeiraParcela: formatarMoeda(detalhes.valorPrimeiraParcela),
      valorBruto: formatarMoeda(detalhes.valorBrutoResgate),
      valorIR: formatarImpostoComAliquota(detalhes.valorImpostoRenda, detalhes.valorAliquotaEfetiva),
      valorLiquido: formatarMoeda(detalhes.valorLiquidoResgate),
    };
  });

  ngOnInit(): void {
    void this.carregarDetalhes();
    void this.getExtratoDesligamento();
  }

  protected async carregarDetalhes(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getDetalhes();
      if (!body) throw new Error('Detalhes sem corpo');
      this.detalhes.set(body);
      this.carregarTermo(body.termoPdf);
      // Com a documentação não validada, já avisa o participante e oferece o reenvio.
      this.openDocModal.set(body.statusSolicitacao === this.statusDocumentacaoNaoValidada);
    } catch {
      this.toastService.show('Não foi possível carregar os detalhes. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /**
   * Monta o PDF do termo a partir do base64 de getDetalhes. Sem o campo (a API ainda não o envia)
   * ou com base64 inválido, o visualizador fica vazio e o restante da página segue normal.
   */
  private carregarTermo(termoPdf: string | undefined): void {
    if (!termoPdf) return;

    try {
      const url = URL.createObjectURL(base64ParaPdf(termoPdf));
      this.pdfSrcTermo.set(url);
      this.destroyRef.onDestroy(() => URL.revokeObjectURL(url));
    } catch {
      this.toastService.show('Não foi possível carregar o termo.', 'error');
    }
  }

  async download(): Promise<void> {
    const endereco = this.pdfSrc();

    if (!endereco) {
      this.toastService.show('Não há extrato disponível para download.', 'error');
      return;
    }

    try {
      await this.baixarArquivo(endereco);
    } catch {
      this.toastService.show('Não foi possível baixar o extrato.', 'error');
    }
  }

  closeModal(): void {
    this.openModal.set(false);
  }

  closeDocModal(): void {
    this.openDocModal.set(false);
  }

  async cancelar(): Promise<void> {
    if (this.cancelamentoBloqueado()) return;

    this.loaderService.showLoader('Cancelando...');
    try {
      await this.httpClientService.cancelarEscolhaInstituto();
      this.toastService.show('A escolha de Instituto Online foi cancelada com sucesso.', 'success');
      this.router.navigate(['instituto-online/home']);
    } catch (erro) {
      console.error('Falha ao cancelar o Instituto Online', erro);
      this.toastService.show('Não foi possível cancelar a escolha de Instituto Online. Tente novamente.', 'error');
    } finally {
      this.loaderService.hideLoader();
      this.closeModal();
    }
  }

  async onSubmit() {
    if (this.cancelamentoBloqueado()) return;
    this.openModal.set(true);
  }

  openDocPage() {
    this.router.navigate(['/instituto-online/upload-documentos']);
  }

  private async baixarArquivo(endereco: string): Promise<void> {
    const resposta = await fetch(endereco);
    const blob = await resposta.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'extrato-desligamento.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(objectUrl);
  }

  private async getExtratoDesligamento(): Promise<void> {
    this.loaderService.showLoader('Gerando extrato...');

    try {
      const { body }: HttpResponse<ExtratoDesligamentoTokenResponse> =
        await this.httpClientService.gerarExtratoDesligamentoPorToken();
      this.pdfSrc.set(body?.urlExtrato);
    } catch (erro) {
      const semTokenValido = erro instanceof HttpErrorResponse && (erro.status === 401 || erro.status === 403);
      this.toastService.show(
        semTokenValido
          ? 'Não foi possível identificar os dados do token.'
          : 'Não foi possível gerar o extrato.',
        'error',
      );
    } finally {
      this.loaderService.hideLoader();
    }
  }
}
